/**
 * DOT Parser Utility
 *
 * Parses Graphviz DOT format files and converts them to React Flow
 * nodes and edges for interactive graph visualization.
 */

// Node type mapping from DOT shapes/colors to our types
const SHAPE_TO_TYPE = {
  'box': 'program',
  'note': 'copybook',
  'cylinder': 'file',
  'ellipse': 'paragraph',
  'diamond': 'decision',
  'hexagon': 'section',
}

// Color to type mapping (fallback)
const COLOR_TO_TYPE = {
  '#4a90d9': 'program',
  '#6b8e23': 'copybook',
  '#d4a574': 'file',
  '#e74c3c': 'entry',
  '#3498db': 'section',
  '#2ecc71': 'paragraph',
  '#95a5a6': 'exit',
  '#9b59b6': 'call',
}

/**
 * Parse a DOT file content into React Flow format
 * @param {string} dotContent - The DOT file content
 * @returns {{ nodes: Array, edges: Array, clusters: Array }}
 */
export function parseDotFile(dotContent) {
  const nodes = []
  const edges = []
  const clusters = []
  const nodeMap = new Map()

  // Clean up the content
  const lines = dotContent.split('\n').map(line => line.trim())

  let currentCluster = null

  for (const line of lines) {
    // Skip empty lines and comments
    if (!line || line.startsWith('//') || line === '{' || line === '}') continue

    // Detect cluster start
    const clusterMatch = line.match(/subgraph\s+cluster_(\w+)\s*\{/)
    if (clusterMatch) {
      currentCluster = {
        id: `cluster_${clusterMatch[1]}`,
        name: clusterMatch[1],
        label: '',
        nodes: [],
      }
      continue
    }

    // Detect cluster end
    if (line === '}' && currentCluster) {
      clusters.push(currentCluster)
      currentCluster = null
      continue
    }

    // Parse cluster label
    if (currentCluster && line.startsWith('label=')) {
      const labelMatch = line.match(/label="([^"]+)"/)
      if (labelMatch) {
        currentCluster.label = labelMatch[1]
      }
      continue
    }

    // Parse edge definitions (A -> B)
    const edgeMatch = line.match(/^(\w+)\s*->\s*(\w+)\s*(\[.*\])?;?$/)
    if (edgeMatch) {
      const [, source, target, attrs] = edgeMatch
      const edge = parseEdge(source, target, attrs)
      edges.push(edge)
      continue
    }

    // Parse node definitions (NodeName [attrs])
    const nodeMatch = line.match(/^(\w+)\s*\[(.+)\];?$/)
    if (nodeMatch) {
      const [, nodeId, attrs] = nodeMatch
      // Skip if it's a global node/edge default
      if (nodeId === 'node' || nodeId === 'edge') continue

      const node = parseNode(nodeId, attrs, currentCluster)
      if (!nodeMap.has(nodeId)) {
        nodes.push(node)
        nodeMap.set(nodeId, node)

        if (currentCluster) {
          currentCluster.nodes.push(nodeId)
        }
      }
      continue
    }
  }

  return { nodes, edges, clusters }
}

/**
 * Parse node attributes from DOT format
 */
function parseNode(id, attrsString, cluster) {
  const attrs = parseAttributes(attrsString)

  // Determine node type from shape or color
  let nodeType = 'default'
  if (attrs.shape && SHAPE_TO_TYPE[attrs.shape]) {
    nodeType = SHAPE_TO_TYPE[attrs.shape]
  } else if (attrs.fillcolor && COLOR_TO_TYPE[attrs.fillcolor]) {
    nodeType = COLOR_TO_TYPE[attrs.fillcolor]
  }

  // Parse label - may contain newlines
  let label = attrs.label || id
  let sublabel = null
  if (label.includes('\\n')) {
    const parts = label.split('\\n')
    label = parts[0]
    sublabel = parts.slice(1).join(' ')
  }

  return {
    id,
    type: 'dependency', // Our custom React Flow node type
    position: { x: 0, y: 0 }, // Will be set by layout
    data: {
      label,
      sublabel,
      nodeType,
      fillColor: attrs.fillcolor || '#8b949e',
      fontColor: attrs.fontcolor || 'white',
      shape: attrs.shape || 'box',
      cluster: cluster?.id || null,
      clusterLabel: cluster?.label || null,
      raw: attrs,
    },
  }
}

/**
 * Parse edge attributes from DOT format
 */
function parseEdge(source, target, attrsString) {
  const attrs = attrsString ? parseAttributes(attrsString.slice(1, -1)) : {}

  // Determine edge style
  const isDashed = attrs.style === 'dashed'
  const isAnimated = attrs.animated === 'true'

  return {
    id: `${source}-${target}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    source,
    target,
    type: 'smoothstep',
    animated: isAnimated,
    style: {
      stroke: attrs.color || '#666',
      strokeWidth: parseFloat(attrs.penwidth) || 1,
      strokeDasharray: isDashed ? '5,5' : undefined,
    },
    label: attrs.label || undefined,
    labelStyle: {
      fill: '#ccc',
      fontSize: 10,
    },
    data: {
      edgeType: attrs.label || 'default',
      raw: attrs,
    },
  }
}

/**
 * Parse attribute string into object
 * e.g., 'label="Foo", shape=box, fillcolor="#4a90d9"'
 */
function parseAttributes(attrsString) {
  const attrs = {}
  if (!attrsString) return attrs

  // Match key=value or key="value" pairs
  const regex = /(\w+)=(?:"([^"]*)"|([^\s,\]]+))/g
  let match

  while ((match = regex.exec(attrsString)) !== null) {
    const [, key, quotedValue, unquotedValue] = match
    attrs[key] = quotedValue !== undefined ? quotedValue : unquotedValue
  }

  return attrs
}

/**
 * Parse cross_reference.json into React Flow format
 * Alternative to DOT parsing for more structured data
 */
export function parseCrossReference(crossRef, mainProgram = 'IFPR321') {
  const nodes = []
  const edges = []
  const nodeMap = new Map()

  // Add main program node
  const mainId = mainProgram.replace('.CBL', '')
  nodes.push({
    id: mainId,
    type: 'dependency',
    position: { x: 0, y: 0 },
    data: {
      label: mainId,
      sublabel: 'Main Program',
      nodeType: 'program',
      fillColor: '#4a90d9',
    },
  })
  nodeMap.set(mainId, true)

  // Add copybook nodes
  if (crossRef.copybook_usage) {
    Object.keys(crossRef.copybook_usage).forEach((copybook, index) => {
      const id = copybook.replace(/-/g, '_')
      if (!nodeMap.has(id)) {
        nodes.push({
          id,
          type: 'dependency',
          position: { x: 0, y: 0 },
          data: {
            label: copybook,
            nodeType: 'copybook',
            fillColor: '#6b8e23',
          },
        })
        nodeMap.set(id, true)

        // Add edge from main to copybook
        edges.push({
          id: `${mainId}-${id}`,
          source: mainId,
          target: id,
          type: 'smoothstep',
          style: { stroke: '#6b8e23', strokeDasharray: '5,5' },
          label: 'COPY',
        })
      }
    })
  }

  // Add call graph edges
  if (crossRef.call_graph) {
    Object.entries(crossRef.call_graph).forEach(([source, targets]) => {
      const sourceId = source.replace('.CBL', '').replace(/-/g, '_')

      targets.forEach((target, index) => {
        const targetId = target.replace(/-/g, '_')

        if (!nodeMap.has(targetId)) {
          nodes.push({
            id: targetId,
            type: 'dependency',
            position: { x: 0, y: 0 },
            data: {
              label: target,
              nodeType: 'call',
              fillColor: '#9b59b6',
            },
          })
          nodeMap.set(targetId, true)
        }

        edges.push({
          id: `${sourceId}-${targetId}-${index}`,
          source: sourceId,
          target: targetId,
          type: 'smoothstep',
          style: { stroke: '#9b59b6' },
          label: 'CALL',
        })
      })
    })
  }

  return { nodes, edges, clusters: [] }
}

/**
 * Parse procedure_model.json into a call graph
 */
export function parseProcedureModel(procedureModel) {
  const nodes = []
  const edges = []
  const nodeMap = new Map()

  if (!procedureModel.paragraphs) return { nodes, edges, clusters: [] }

  // Create nodes for each paragraph
  procedureModel.paragraphs.forEach((para, index) => {
    const id = para.name.replace(/-/g, '_')

    // Determine node type based on name
    let nodeType = 'paragraph'
    if (para.name.includes('MAIN') || para.name.startsWith('000-')) {
      nodeType = 'entry'
    } else if (para.name.includes('EXIT') || para.name.includes('CLOSE')) {
      nodeType = 'exit'
    } else if (para.name.includes('OPEN') || para.name.includes('READ') || para.name.includes('WRITE')) {
      nodeType = 'file'
    }

    const colors = {
      entry: '#e74c3c',
      exit: '#95a5a6',
      paragraph: '#3498db',
      file: '#d4a574',
    }

    nodes.push({
      id,
      type: 'dependency',
      position: { x: 0, y: 0 },
      data: {
        label: para.name,
        sublabel: para.java_name,
        nodeType,
        fillColor: colors[nodeType] || '#3498db',
        startLine: para.start_line,
        endLine: para.end_line,
        statementCount: para.statements?.length || 0,
      },
    })
    nodeMap.set(id, para)

    // Extract PERFORM targets from statements
    if (para.statements) {
      para.statements.forEach(stmt => {
        if (stmt.semantic?.type === 'PERFORM' && stmt.semantic.target) {
          const targetId = stmt.semantic.target.replace(/-/g, '_')

          edges.push({
            id: `${id}-${targetId}-${stmt.line_num}`,
            source: id,
            target: targetId,
            type: 'smoothstep',
            style: { stroke: '#666' },
            label: stmt.semantic.thru ? `THRU ${stmt.semantic.thru}` : undefined,
            data: {
              lineNum: stmt.line_num,
              thru: stmt.semantic.thru,
            },
          })
        }
      })
    }
  })

  return { nodes, edges, clusters: [] }
}

export default {
  parseDotFile,
  parseCrossReference,
  parseProcedureModel,
}
